/**
 * Display vocabulary for the v2 console — one place for every wire-enum → zh label / tone
 * mapping, so the roster table, lookup drawer, dashboard and import preview never disagree.
 * Colours are token names (var(--…)), never literals.
 */
import type { BadgeTone } from '@/components/Badge';
import type {
  AuditActorType,
  AuditCategory,
  LicenseState,
  PreviewCategory,
  Role,
  RosterOrigin,
  RosterStatus,
  TicketStatus,
} from '@/types';

export const ORIGIN_ORDER: RosterOrigin[] = ['serial', 'manual', 'import', 'machine_approval', 'unknown'];

export const ORIGIN_LABEL: Record<RosterOrigin, string> = {
  serial: '序號兌換',
  manual: '手動加入',
  import: '批次匯入',
  machine_approval: '機器核准',
  unknown: '無法判定',
};

/** Swatch colour per origin (legend dots + the stacked origin bar). */
export const ORIGIN_COLOR: Record<RosterOrigin, string> = {
  serial: 'var(--primary)',
  manual: 'var(--orange)',
  import: 'var(--green)',
  machine_approval: 'var(--purple)',
  unknown: 'var(--text-subtle)',
};

export const ORIGIN_TONE: Record<RosterOrigin, BadgeTone> = {
  serial: 'info',
  manual: 'warning',
  import: 'success',
  machine_approval: 'neutral',
  unknown: 'neutral',
};

export const STATUS_ORDER: RosterStatus[] = ['active', 'revoked', 'rejected', 'expired'];

export const STATUS_LABEL: Record<RosterStatus, string> = {
  active: '有效',
  revoked: '已撤銷',
  rejected: '已拒絕',
  expired: '已到期',
};

export const STATUS_TONE: Record<RosterStatus, BadgeTone> = {
  active: 'success',
  revoked: 'neutral',
  rejected: 'danger',
  expired: 'warning',
};

/** Text colour for the plain-text status cell in the roster table. */
export const STATUS_TEXT_COLOR: Record<RosterStatus, string> = {
  active: 'var(--success-text)',
  revoked: 'var(--text-muted)',
  rejected: 'var(--error-text)',
  expired: 'var(--warning-text)',
};

export const PREVIEW_LABEL: Record<PreviewCategory, string> = {
  add: '新增',
  update: '會更新欄位',
  same: '已存在且相同',
  revoked: '已撤銷',
  invalid: '格式錯誤',
  duplicate: '檔內重複',
};

export const PREVIEW_TONE: Record<PreviewCategory, BadgeTone> = {
  add: 'success',
  update: 'info',
  same: 'neutral',
  revoked: 'danger',
  invalid: 'warning',
  duplicate: 'warning',
};

export const PREVIEW_ORDER: PreviewCategory[] = ['add', 'update', 'same', 'revoked', 'invalid', 'duplicate'];

/** Labels for the import fields, used in change descriptions and the column mapper. */
export const IMPORT_FIELD_LABEL: Record<string, string> = {
  sn: '硬體序號',
  owner_name: '擁有人',
  owner_email: 'Email',
  team: '團隊',
  notes: '備註',
  expires_at: '到期日',
};

/** "團隊「PM」→「產品」;補上 Email" — the preview's 說明 column for an update row. */
export function describeChanges(changes: Record<string, [string | null, string | null]> | undefined): string {
  if (!changes) return '';
  const parts: string[] = [];
  for (const [field, [from, to]] of Object.entries(changes)) {
    const label = IMPORT_FIELD_LABEL[field] ?? field;
    // A Latin label (Email) gets a space after the CJK verb: 「補上 Email」, 「補上團隊」.
    if (from === null || from === '') parts.push(/^[A-Za-z]/.test(label) ? `補上 ${label}` : `補上${label}`);
    else parts.push(`${label}「${from}」→「${to ?? ''}」`);
  }
  return parts.join(';');
}

export const LICENSE_STATE_LABEL: Record<LicenseState, string> = {
  minted: '未兌換',
  activated: '已兌換',
  revoked: '已撤銷',
};

export const LICENSE_STATE_TONE: Record<LicenseState, BadgeTone> = {
  minted: 'neutral',
  activated: 'info',
  revoked: 'danger',
};

export const TICKET_STATUS_ORDER: TicketStatus[] = ['open', 'investigating', 'waiting_user', 'resolved', 'closed'];

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  open: '待處理',
  investigating: '調查中',
  waiting_user: '等待用戶',
  resolved: '已解決',
  closed: '已結案',
};

export const TICKET_STATUS_TONE: Record<TicketStatus, BadgeTone> = {
  open: 'warning',
  investigating: 'info',
  waiting_user: 'neutral',
  resolved: 'success',
  closed: 'neutral',
};

/** Unknown wire values (an old ticket status, a new one from a later Worker) fall back to the raw value. */
export function ticketStatusLabel(s: string): string {
  return (TICKET_STATUS_LABEL as Record<string, string>)[s] ?? s;
}
export function ticketStatusTone(s: string): BadgeTone {
  return (TICKET_STATUS_TONE as Record<string, BadgeTone>)[s] ?? 'neutral';
}

export const SOURCE_LABEL: Record<AuditActorType, string> = {
  admin: '後台',
  app: 'App',
  system: '系統',
};

export const SOURCE_TONE: Record<AuditActorType, BadgeTone> = {
  admin: 'info',
  app: 'neutral',
  system: 'warning',
};

export const AUDIT_CATEGORY_LABEL: Record<AuditCategory, string> = {
  roster: '名單',
  model: '機型',
  license: '軟體序號',
  support: '客服',
  account: '帳號',
};

const ACTION_LABEL: Record<string, string> = {
  'whitelist.add': '名單:新增',
  'whitelist.revoke': '名單:撤銷',
  'whitelist.restore': '名單:復原',
  'whitelist.meta_update': '名單:更新欄位',
  'whitelist.expiry': '名單:設定到期日',
  'whitelist.import_add': '名單:匯入新增',
  'whitelist.import_update': '名單:匯入更新',
  'whitelist.import_restore': '名單:匯入復原',
  'whitelist.import_commit': '名單:批次匯入',
  'whitelist.import_expired': '名單:匯入批次失效',
  'machine.approve': '名單:核准機器',
  'machine.reject': '名單:拒絕機器',
  'model_whitelist.add': '機型:新增',
  'model_whitelist.update': '機型:更新',
  'model_whitelist.revoke': '機型:撤銷',
  'model_whitelist.restore': '機型:復原',
  'license.activate': '序號:兌換',
  'license.repair_admission': '序號:找回',
  'license.mint': '序號:鑄造批次',
  'license.revoke': '序號:撤銷',
  'support.ticket_create': '客服:新工單',
  'support.message_append': '客服:補充訊息',
  'support.ticket.status': '客服:改狀態',
  'support.ticket.assignee': '客服:指派',
  'support.ticket.reply': '客服:回覆',
  'support.ticket.note': '客服:內部備註',
  'admin_user.set_role': '帳號:變更角色',
  'metrics.aggregate': '每日彙整',
  'retention.prune': '保留期清理',
  'alert.status': '警報:改狀態',
  'alert.notes': '警報:備註',
  // app-api writes these on every App sign-in / token refresh (routine; hidden by default).
  'device.register': '裝置:首次連線',
  'device.refresh_rotate': '裝置:重新註冊',
  'device.token_refresh': '裝置:更新連線憑證',
};

const ACTION_PREFIX: [string, string][] = [
  ['device.', '裝置'],
  ['whitelist.', '名單'],
  ['machine.', '名單'],
  ['model_whitelist.', '機型'],
  ['license.', '序號'],
  ['support.', '客服'],
  ['admin_user.', '帳號'],
];

/** Human label for an audit action; unknown actions keep their raw id behind the area prefix. */
export function auditActionLabel(action: string): string {
  const known = ACTION_LABEL[action];
  if (known) return known;
  const hit = ACTION_PREFIX.find(([p]) => action.startsWith(p));
  return hit ? `${hit[1]}:${action.slice(hit[0].length)}` : action || '—';
}

export const ROLE_LABEL_ZH: Record<Role, string> = {
  super_admin: '超級管理員',
  admin: '管理員',
  support: '客服',
  read_only: '唯讀',
};

/** Percent with no decimals; 0 when the denominator is 0. */
export function pct(n: number, d: number): number {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

/** Import row error / skip reason codes (admin-api) → zh. `too_long:<field>` and
 *  `invalid_field:<field>` carry the field name after the colon. */
export function importReasonLabel(code: string | undefined | null): string {
  if (!code) return '';
  const [head, field] = code.split(':', 2);
  const f = field ? IMPORT_FIELD_LABEL[field] ?? field : '';
  switch (head) {
    case 'invalid_sn': return '硬體序號格式不符';
    case 'invalid_email': return 'Email 格式不符';
    case 'invalid_date': return '到期日格式不符';
    case 'too_long': return `${f || '欄位'}太長`;
    case 'invalid_field': return `${f || '欄位'}內容不符`;
    case 'same': return '已存在且相同';
    case 'duplicate': return '檔內重複';
    case 'revoked_not_selected': return '已撤銷,未勾選復原';
    case 'appeared_concurrently': return '寫入時已被加入';
    default: return code;
  }
}
